import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Spinner } from 'react-bootstrap';
import { FaFilm, FaTv, FaMusic, FaCalendarAlt, FaStar, FaChartLine } from 'react-icons/fa';
import dataService from '../services/dataService';
import { parseGenres } from '../utils/dataUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import { error as logError } from '../utils/logger';
import '../styles/UserStats.css';

const TYPE_COLORS = {
  movie: '#667eea',
  series: '#764ba2',
  music: '#f093fb',
};

const BAR_TRACK_PX = 132;

const RATING_COLORS = {
  '1-2': '#dc3545',
  '3-4': '#fd7e14',
  '5-6': '#ffc107',
  '7-8': '#20c997',
  '9-10': '#198754',
};

function buildDonutGradient(typeStats) {
  const total = typeStats.reduce((s, x) => s + x.value, 0);
  if (!total) return 'conic-gradient(#e8ecf4 0deg 360deg)';
  let angle = 0;
  const stops = [];
  typeStats.forEach((item) => {
    const color = TYPE_COLORS[item.key] || '#667eea';
    const slice = (item.value / total) * 360;
    stops.push(`${color} ${angle}deg ${angle + slice}deg`);
    angle += slice;
  });
  return `conic-gradient(${stops.join(', ')})`;
}

const UserStats = ({ stats }) => {
  const { language } = useLanguage();
  const [detailedStats, setDetailedStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDetailedStats();
  }, [language]);

  const loadDetailedStats = async () => {
    try {
      setLoading(true);
      const [movies, series, music] = await Promise.all([
        dataService.getWatchedMovies(),
        dataService.getWatchedSeries(),
        dataService.getListenedMusic(),
      ]);
      const allContent = [...movies, ...series, ...music];
      setDetailedStats({
        genreStats: calculateGenreStats(allContent),
        ratingStats: calculateRatingStats(allContent),
        monthlyStats: calculateMonthlyStats(allContent, language),
        typeStats: calculateTypeStats(movies, series, music, language),
        thisMonthStats: calculateThisMonthStats(allContent),
        artistStats: calculateArtistStats(music),
        highRatingCount: countHighRatings(allContent),
      });
    } catch (error) {
      logError('Error loading detailed statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateGenreStats = (content) => {
    const genreCount = {};
    content.forEach((item) => {
      parseGenres(item.genre).forEach((g) => {
        const key = g.trim();
        if (key) genreCount[key] = (genreCount[key] || 0) + 1;
      });
    });
    return Object.entries(genreCount)
      .map(([genre, count]) => ({ genre, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const calculateRatingStats = (content) => {
    const ratingRanges = { '1-2': 0, '3-4': 0, '5-6': 0, '7-8': 0, '9-10': 0 };
    content.forEach((item) => {
      const rating = item.ratings?.user_rating ?? item.user_rating ?? 0;
      if (rating >= 1 && rating <= 2) ratingRanges['1-2']++;
      else if (rating >= 3 && rating <= 4) ratingRanges['3-4']++;
      else if (rating >= 5 && rating <= 6) ratingRanges['5-6']++;
      else if (rating >= 7 && rating <= 8) ratingRanges['7-8']++;
      else if (rating >= 9 && rating <= 10) ratingRanges['9-10']++;
    });
    return Object.entries(ratingRanges).map(([range, count]) => ({ range, count }));
  };

  const getDateFromItem = (item) =>
    item.watched_date || item.date_watched || item.listened_date || null;

  const calculateMonthlyStats = (content, lang) => {
    const byKey = {};
    content.forEach((item) => {
      const dateStr = getDateFromItem(item);
      if (!dateStr) return;
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byKey[key]) {
        const locale = lang === 'en' ? 'en-US' : 'es-ES';
        byKey[key] = {
          monthKey: key,
          monthLabel: d.toLocaleDateString(locale, { month: 'short' }),
          yearLabel: String(d.getFullYear()),
          count: 0,
        };
      }
      byKey[key].count += 1;
    });
    return Object.values(byKey)
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .slice(-12);
  };

  const calculateThisMonthStats = (content) => {
    const now = new Date();
    let count = 0;
    content.forEach((item) => {
      const dateStr = getDateFromItem(item);
      if (!dateStr) return;
      const d = new Date(dateStr);
      if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) count += 1;
    });
    return count;
  };

  const calculateTypeStats = (movies, series, music, lang) => {
    const total = movies.length + series.length + music.length;
    return [
      {
        key: 'movie',
        name: t('stats.movies', lang),
        value: movies.length,
        color: TYPE_COLORS.movie,
        pct: total ? (movies.length / total) * 100 : 0,
      },
      {
        key: 'series',
        name: t('stats.series', lang),
        value: series.length,
        color: TYPE_COLORS.series,
        pct: total ? (series.length / total) * 100 : 0,
      },
      {
        key: 'music',
        name: t('stats.music', lang),
        value: music.length,
        color: TYPE_COLORS.music,
        pct: total ? (music.length / total) * 100 : 0,
      },
    ];
  };

  const countHighRatings = (content) => {
    return content.filter((item) => {
      const r = Number(item.ratings?.user_rating ?? item.user_rating ?? 0);
      return r >= 9;
    }).length;
  };

  const calculateArtistStats = (music) => {
    const byArtist = {};
    (music || []).forEach((item) => {
      const name = (item.artist || '').trim();
      if (name) byArtist[name] = (byArtist[name] || 0) + 1;
    });
    return Object.entries(byArtist)
      .map(([artist, count]) => ({ artist, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  };

  const maxMonthly = useMemo(
    () => Math.max(...(detailedStats?.monthlyStats?.map((m) => m.count) || [0]), 1),
    [detailedStats]
  );
  const maxGenre = detailedStats?.genreStats?.[0]?.count || 1;
  const maxRating = Math.max(...(detailedStats?.ratingStats?.map((r) => r.count) || [0]), 1);
  const maxArtist = detailedStats?.artistStats?.[0]?.count || 1;
  const topGenre = detailedStats?.genreStats?.[0]?.genre;
  const avgRating = Number(stats?.averageRating) || 0;
  const totalWatched = Number(stats?.totalWatched) || 0;

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">{t('stats.loading', language)}</p>
      </div>
    );
  }

  if (!stats || !detailedStats) {
    return (
      <Card className="stats-panel text-center py-5">
        <Card.Body>
          <h5>{t('stats.noStatsAvailable', language)}</h5>
          <p className="text-muted mb-0">{t('stats.markContentToSeeStats', language)}</p>
        </Card.Body>
      </Card>
    );
  }

  const donutStyle = {
    background: buildDonutGradient(detailedStats.typeStats),
  };

  return (
    <div className="stats-page">
      <header className="stats-page-header">
        <h2>{t('stats.title', language)}</h2>
        <p className="text-muted mb-0">{t('stats.subtitle', language)}</p>
      </header>

      <div className="stats-kpi-grid">
        <Card className="stats-kpi-card stats-kpi-card--primary">
          <Card.Body className="py-3">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="stats-kpi-value">{totalWatched}</div>
                <div className="stats-kpi-label">{t('stats.totalWatched', language)}</div>
              </div>
              <FaChartLine className="stats-kpi-icon" />
            </div>
          </Card.Body>
        </Card>

        <Card className="stats-kpi-card stats-kpi-card--surface">
          <Card.Body className="py-3">
            <div className="stats-kpi-value text-primary">{detailedStats.thisMonthStats}</div>
            <div className="stats-kpi-label text-muted">
              <FaCalendarAlt className="me-1" />
              {t('stats.thisMonth', language)}
            </div>
          </Card.Body>
        </Card>

        <Card className="stats-kpi-card stats-kpi-card--surface">
          <Card.Body className="py-3">
            <div className="stats-kpi-value" style={{ color: 'var(--miskosas-secondary)' }}>
              {avgRating > 0 ? avgRating.toFixed(1) : '—'}
            </div>
            <div className="stats-kpi-label text-muted">
              <FaStar className="me-1 text-warning" />
              {t('stats.averageRating', language)}
            </div>
          </Card.Body>
        </Card>

        <Card className="stats-kpi-card stats-kpi-card--surface">
          <Card.Body className="py-3">
            <div className="stats-kpi-value text-success">{detailedStats.highRatingCount}</div>
            <div className="stats-kpi-label text-muted">{t('stats.highRatings', language)}</div>
          </Card.Body>
        </Card>

        <Card className="stats-kpi-card stats-kpi-card--surface">
          <Card.Body className="py-3 d-flex align-items-center gap-2">
            <FaFilm className="text-primary" />
            <div>
              <div className="fw-bold">{stats.moviesWatched}</div>
              <div className="stats-kpi-label text-muted">{t('stats.movies', language)}</div>
            </div>
          </Card.Body>
        </Card>

        <Card className="stats-kpi-card stats-kpi-card--surface">
          <Card.Body className="py-3 d-flex align-items-center gap-2">
            <FaTv className="text-success" />
            <div>
              <div className="fw-bold">{stats.seriesWatched}</div>
              <div className="stats-kpi-label text-muted">{t('stats.series', language)}</div>
            </div>
          </Card.Body>
        </Card>

        <Card className="stats-kpi-card stats-kpi-card--surface">
          <Card.Body className="py-3 d-flex align-items-center gap-2">
            <FaMusic style={{ color: TYPE_COLORS.music }} />
            <div>
              <div className="fw-bold">{stats.musicListened}</div>
              <div className="stats-kpi-label text-muted">{t('stats.music', language)}</div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {topGenre && (
        <p className="mb-4">
          <span className="stats-genre-chip">
            {t('stats.topGenre', language)}: {topGenre}
          </span>
        </p>
      )}

      <Row className="g-4 mb-4">
        <Col lg={7}>
          <Card className="stats-panel">
            <Card.Body>
              <h5 className="card-title">{t('stats.monthlyActivity', language)}</h5>
              {detailedStats.monthlyStats.length === 0 ? (
                <p className="text-muted small mb-0">{t('stats.noMonthlyActivity', language)}</p>
              ) : (
                <div
                  className="stats-bar-chart"
                  role="list"
                  aria-label={t('stats.monthlyActivity', language)}
                >
                  {detailedStats.monthlyStats.map((item) => {
                    const barPx = Math.max(
                      8,
                      Math.round((item.count / maxMonthly) * BAR_TRACK_PX)
                    );
                    return (
                      <div
                        key={item.monthKey}
                        className="stats-bar-col"
                        role="listitem"
                        title={`${item.monthLabel} ${item.yearLabel}: ${item.count}`}
                      >
                        <span className="stats-bar-value" aria-hidden="false">
                          {item.count}
                        </span>
                        <div className="stats-bar-track" aria-hidden="true">
                          <div className="stats-bar" style={{ height: `${barPx}px` }} />
                        </div>
                        <span className="stats-bar-label">
                          {item.monthLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="stats-panel">
            <Card.Body>
              <h5 className="card-title">{t('stats.distributionByType', language)}</h5>
              {detailedStats.typeStats.every((x) => x.value === 0) ? (
                <p className="text-muted small mb-0">{t('stats.noContentByType', language)}</p>
              ) : (
                <div className="stats-donut-wrap">
                  <div className="stats-donut" style={donutStyle}>
                    <div className="stats-donut-hole">
                      <span>{totalWatched}</span>
                      <span className="small text-muted fw-normal" style={{ fontSize: '0.65rem' }}>
                        {t('stats.items', language)}
                      </span>
                    </div>
                  </div>
                  <div className="stats-legend">
                    {detailedStats.typeStats.map((item) => (
                      <div key={item.key} className="stats-legend-item">
                        <span className="d-flex align-items-center">
                          <span className="stats-legend-dot" style={{ background: item.color }} />
                          {item.name}
                        </span>
                        <strong>
                          {item.value} ({Math.round(item.pct)}%)
                        </strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col md={5}>
          <Card className="stats-panel h-100">
            <Card.Body>
              <h5 className="card-title">{t('stats.averageRating', language)}</h5>
              <div className="stats-rating-hero">
                <div className="stats-rating-circle">{avgRating > 0 ? avgRating.toFixed(1) : '—'}</div>
                <div>
                  <p className="mb-2 fw-semibold">{t('stats.fromAllWatched', language)}</p>
                  {detailedStats.ratingStats.every((r) => r.count === 0) ? (
                    <p className="text-muted small mb-0">{t('stats.noRatingsYet', language)}</p>
                  ) : (
                    detailedStats.ratingStats.map((item) => (
                      <div key={item.range} className="stats-h-bar-row">
                        <div className="d-flex justify-content-between small mb-1">
                          <span>{item.range}</span>
                          <strong>{item.count}</strong>
                        </div>
                        <div className="stats-h-bar-track">
                          <div
                            className="stats-h-bar-fill"
                            style={{
                              width: `${(item.count / maxRating) * 100}%`,
                              background: RATING_COLORS[item.range],
                            }}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={7}>
          <Card className="stats-panel h-100">
            <Card.Body>
              <h5 className="card-title">{t('stats.favoriteGenres', language)}</h5>
              {detailedStats.genreStats.length === 0 ? (
                <p className="text-muted small mb-0">{t('stats.markContentToSeeGenres', language)}</p>
              ) : (
                <>
                  <div className="mb-3">
                    {detailedStats.genreStats.slice(0, 6).map((item) => (
                      <span key={item.genre} className="stats-genre-chip">
                        {item.genre} <strong>{item.count}</strong>
                      </span>
                    ))}
                  </div>
                  {detailedStats.genreStats.slice(0, 8).map((item) => (
                    <div key={item.genre} className="stats-h-bar-row">
                      <div className="d-flex justify-content-between small mb-1">
                        <span>{item.genre}</span>
                        <strong>{item.count}</strong>
                      </div>
                      <div className="stats-h-bar-track">
                        <div
                          className="stats-h-bar-fill"
                          style={{
                            width: `${(item.count / maxGenre) * 100}%`,
                            background: 'var(--miskosas-primary)',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {detailedStats.artistStats.length > 0 && (
        <Row>
          <Col>
            <Card className="stats-panel">
              <Card.Body>
                <h5 className="card-title">{t('stats.mostListenedArtists', language)}</h5>
                {detailedStats.artistStats.map((item) => (
                  <div key={item.artist} className="stats-h-bar-row">
                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-truncate me-2">{item.artist}</span>
                      <strong>{item.count}</strong>
                    </div>
                    <div className="stats-h-bar-track">
                      <div
                        className="stats-h-bar-fill"
                        style={{
                          width: `${(item.count / maxArtist) * 100}%`,
                          background: TYPE_COLORS.music,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default UserStats;
